// frontend/src/components/layout/CintaOpciones.js
// REPLICA EXACTA DE cinta_opciones.html CON TEMAS REACTIVOS
import React from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

// CONTENEDOR DE LA CINTA (REPLICA EL NAV ORIGINAL)
const CintaContainer = styled.nav`
  display: flex;
  flex-shrink: 0;
  background-color: ${props => props.theme.colors.bgSecundario};
  border-bottom: 1px solid ${props => props.theme.colors.borderDefault};
  width: 100%;
  height: auto;
  padding: 0.25rem 0;
  box-shadow: ${props => props.theme.shadows.sm};

  /* Ocultar cinta de opciones al imprimir */
  @media print {
    display: none !important;
  }
`;

// CONTENEDOR FLUID (REPLICA container-fluid)
const ContainerFluid = styled.div`
  padding: 0.05rem 1rem;
  display: flex;
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0.05rem 0.5rem;
    overflow-x: auto;
    flex-wrap: nowrap;
    /* Scroll táctil fluido y sin barra de scroll visible */
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

// NAVBAR NAV (REPLICA navbar-nav flex-row)
const NavbarNav = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-wrap: nowrap;
    width: max-content;
    gap: 0.35rem;
  }
`;

// BOTÓN CINTA (REPLICA btn btn-outline-options btn-cinta)
// Hover: resalte tenue en celeste (antes amarillo, no combinaba con la paleta).
// Activo ($active, ruta actual): en reposo NO lleva fondo pintado a propósito
// — solo recuadro parejo (mismo grosor en los 4 lados) + ícono + texto en
// celeste (opcionActivaBorde/primary) más el glow, para que se lea como
// "marcado", no como un botón presionado/relleno. El fondo (opcionActivaFondo)
// queda reservado para el :hover — recién ahí, con el mouse encima, se pinta.
const BotonCinta = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.75rem;
  background: ${({ theme }) => theme.colors.transparent};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.opcionActivaBorde : theme.colors.borderDefault)};
  border-radius: ${props => props.theme.borderRadius.sm};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.txtPrincipal)};
  text-decoration: none;
  cursor: pointer;
  box-shadow: ${({ theme, $active }) => ($active ? `0 0 10px ${theme.colors.primaryAlpha}` : 'none')};
  transition-property: background-color, border-color, color, box-shadow;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  min-width: 80px;
  flex-shrink: 0;

  &:hover {
    background-color: ${({ theme, $active }) => ($active ? theme.colors.opcionActivaFondo : theme.colors.primaryAlpha)};
    border-color: ${({ theme, $active }) => ($active ? theme.colors.opcionActivaBorde : theme.colors.primarySoftBorder)};
    color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 12px ${({ theme }) => theme.colors.primaryAlpha};
  }

  i {
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
  }

  span {
    font-size: 0.7rem;
    font-weight: 400;
    line-height: 1;
  }

  /* Botones más compactos en móvil */
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0.35rem 0.55rem;
    min-width: 62px;

    i {
      font-size: 1.2rem;
      margin-bottom: 0.15rem;
    }

    span {
      font-size: 0.62rem;
    }
  }
`;

// Coincidencia de ruta activa consciente de límites de segmento: "/mantenimientos"
// no debe marcar activo a un hipotético "/mantenimientosx", y la home ("/") solo
// se marca activa en match exacto (si no, "empezaría" en todas las rutas).
const esRutaActiva = (pathname, base) => {
  if (base === '/') return pathname === '/';
  return pathname === base || pathname.startsWith(`${base}/`);
};

const CintaOpciones = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();

  // El módulo de informes tiene permiso dedicado
  const puedeVerInformes = hasPermission('configuracioninformes', 'view');

  const opciones = [
    {
      label: 'Equipos',
      icon: 'bi bi-truck',
      path: '/',
      description: 'Gestión de equipos',
      // Además de la home: ficha/alta de equipo y la grilla de flota.
      matchPaths: ['/', '/equipos', '/flota']
    },
    {
      label: 'Reparaciones',
      icon: 'bi bi-tools',
      path: '/reparaciones',
      description: 'Órdenes de trabajo correctivas'
    },
    {
      label: 'Mantenimiento',
      icon: 'bi bi-calendar-check',
      path: '/mantenimientos',
      description: 'Gestión de mantenimientos',
      // Planes, rutinas y tareas de mantenimiento viven en rutas propias
      // (no bajo /mantenimientos), pero son el mismo módulo a ojos del usuario.
      matchPaths: ['/mantenimientos', '/planes-mantenimiento', '/rutinas-mantenimiento', '/tareas-mantenimiento']
    },
    {
      label: 'Logística',
      icon: 'bi bi-bezier2',
      path: '/logistica',
      description: 'Movimientos de transporte de equipos'
    },
    {
      label: 'Rental',
      icon: 'bi bi-cash-coin',
      path: '/rental',
      description: 'Alquiler de equipos a terceros'
    },
    {
      label: 'Almacén',
      icon: 'bi bi-box-seam',
      path: '/almacen',
      description: 'Gestión transaccional de repuestos y stock por obra'
    },
    {
      label: 'Partes',
      icon: 'bi bi-journal-text',
      path: '/partes',
      description: 'Partes diarios'
    },
    {
      label: 'Lubricantes',
      icon: 'bi bi-droplet-fill',
      path: '/lubricantes',
      description: 'Registros de lubricantes (aceites y engrases)'
    },
    {
      label: 'Combustible',
      icon: 'bi bi-fuel-pump',
      path: '/combustible',
      description: 'Registro de despachos de combustible por equipo'
    },
    {
      label: 'Documentación',
      icon: 'bi bi-folder2-open',
      path: '/documentacion',
      description: 'Gestión de documentación'
    },
    ...(puedeVerInformes ? [{
      label: 'Informes',
      icon: 'bi bi-graph-up-arrow',
      path: '/informes',
      description: 'KPIs e informes de gestión de mantenimiento, almacén y logística'
    }] : []),
    // La app móvil no tiene botón acá a propósito. Es un formulario pensado
    // para un teléfono parado al lado de una máquina: al usuario de PC no le
    // sirve entrar, le sirve poder entregársela al operario. Eso vive en
    // Configuración → App para celular, con el QR y la dirección para pasarle.
    {
      label: 'Configuración',
      icon: 'bi bi-gear-fill',
      path: '/configuracion',
      description: 'Configuración del sistema'
    }
  ];

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <CintaContainer id="cinta-opciones">
      <ContainerFluid>
        <NavbarNav>
          {opciones.map((opcion, index) => {
            const rutasAValidar = opcion.matchPaths || [opcion.path];
            const activo = rutasAValidar.some((base) => esRutaActiva(location.pathname, base));
            return (
              <BotonCinta
                key={index}
                $active={activo}
                aria-current={activo ? 'page' : undefined}
                onClick={() => handleNavigate(opcion.path)}
                title={opcion.description}
              >
                <i className={opcion.icon}></i>
                <span>{opcion.label}</span>
              </BotonCinta>
            );
          })}
        </NavbarNav>
      </ContainerFluid>
    </CintaContainer>
  );
};

export default CintaOpciones;